import {
  notificarAssetsSistemaAtualizados,
  sincronizarConfiguracoesSistemaPublicas,
} from "../utils/configuracoesSistema";
import { lerEmpresaLoginSelecionada } from "./empresaLogin";

export interface ConfigVisual {
  fantasia?: string;
  corPerfil?: string;
  corLight?: string;
  marcaEmpresaPersonalizada?: boolean;
  tonalidadeCor?: string | number;
  intensidadeCor?: string | number;
  observacoes?: string | number;
  statusGradiente?: string | boolean;
  modulosAtivos?: string;
  loginSocial?: string | boolean;
  cadastroLogin?: string | boolean;
  tourBoasVindas?: string | boolean;
  bgGradient?: string;
  inverterGradient?: string | boolean;
  botoesRetangulares?: string | boolean;
  glass?: string | boolean;
  glassIntensidade?: string | number;
  bgGradient1XY?: string | number;
  bgGradient2XY?: string | number;
  bgGradient1Vertical?: string | boolean;
  bgGradient2Vertical?: string | boolean;
  bgGradientSize?: string | number;
  bgGradient1X?: string | number;
  bgGradient1Y?: string | number;
  bgGradient1Size?: string | number;
  bgGradient2X?: string | number;
  bgGradient2Y?: string | number;
  bgGradient2Size?: string | number;
  corSidebar?: string;
  opacidadeSidebar?: string | number;
}

const CACHE_KEY = "i9.config-visual";
const BG_DEFAULT_LIGHTEN = 40;

const BG_PALETTES: Record<string, readonly [string, string]> = {
  default: ["", ""],
  "grad-tropical": ["#ffd166", "#ff7b54"],
  "grad-ocean": ["#72ddf7", "#2563eb"],
  "grad-candy": ["#ffb4d9", "#ff6b9d"],
  "grad-sunset": ["#fbd786", "#f7797d"],
  "grad-cosmic": ["#7c3aed", "#0f172a"],
  "grad-mint": ["#99f6e4", "#14b8a6"],
  "grad-flamingo": ["#fda4af", "#fb7185"],
  "grad-aurora": ["#86efac", "#22d3ee"],
  "grad-sky": ["#bfdbfe", "#38bdf8"],
  "grad-fire": ["#fdba74", "#f97316"],
  "grad-berry": ["#c4b5fd", "#7c3aed"],
  "grad-lavender": ["#ddd6fe", "#a78bfa"],
  "grad-jade": ["#86efac", "#16a34a"],
  "grad-rose": ["#fecdd3", "#fb7185"],
  "grad-gold": ["#fde68a", "#f59e0b"],
  "grad-teal": ["#99f6e4", "#0f766e"],
  "grad-blueberry": ["#93c5fd", "#4338ca"],
  "grad-peach": ["#fed7aa", "#fb7185"],
  "grad-neon": ["#67e8f9", "#22c55e"],
};

const SIDEBAR_GRADIENTS: Record<string, readonly string[]> = {
  "grad-tropical": ["#72f1dd", "#ffd166", "#ff7b54"],
  "grad-ocean": ["#72ddf7", "#2563eb"],
  "grad-candy": ["#ffb4d9", "#ff6b9d"],
  "grad-sunset": ["#ffd166", "#f7797d"],
  "grad-cosmic": ["#a78bfa", "#0f172a"],
  "grad-mint": ["#86efac", "#14b8a6"],
  "grad-flamingo": ["#fda4af", "#fb7185"],
  "grad-aurora": ["#ddd6fe", "#22d3ee"],
  "grad-sky": ["#bfdbfe", "#38bdf8"],
  "grad-fire": ["#fdba74", "#f97316"],
  "grad-berry": ["#7c3aed", "#be185d"],
  "grad-lavender": ["#f0abfc", "#a78bfa"],
  "grad-jade": ["#a3e635", "#16a34a"],
  "grad-rose": ["#fb7185", "#9333ea"],
  "grad-gold": ["#fde047", "#fb923c"],
  "grad-teal": ["#99f6e4", "#0f766e"],
  "grad-blueberry": ["#60a5fa", "#6366f1"],
  "grad-peach": ["#fed7aa", "#c084fc"],
  "grad-neon": ["#22d3ee", "#4ade80"],
  "grad-electric": ["#3b82f6", "#1d4ed8"],
  "grad-aqua": ["#22d3ee", "#0891b2"],
  "grad-violet": ["#c084fc", "#a21caf"],
  "grad-coral": ["#fb7185", "#ef4444"],
  "grad-mango": ["#fde68a", "#f59e0b"],
  "grad-steel": ["#67e8f9", "#475569"],
  "grad-orchid": ["#f472b6", "#a21caf"],
  "grad-crimson": ["#fb7185", "#ef4444"],
  "grad-citrus": ["#fde047", "#fb923c"],
  "grad-dusk": ["#818cf8", "#4f46e5"],
  "grad-magma": ["#fb7185", "#7f1d1d"],
  "grad-dawn": ["#fdba74", "#93c5fd"],
  "grad-pumpkin": ["#fb923c", "#a855f7"],
};

function corValida(v: unknown): string {
  const s = String(v || "").trim();
  return /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(s) ? s : "";
}

function separarHexComAlpha(cor: string): { hex: string; alpha: string } {
  const normalizada = corValida(cor).replace("#", "");
  if (normalizada.length === 8) {
    return { hex: `#${normalizada.slice(0, 6)}`, alpha: normalizada.slice(6) };
  }
  return { hex: `#${normalizada.slice(0, 6)}`, alpha: "" };
}

function clarearCor(hex: string, percent: number): string {
  const base = hex.replace("#", "").slice(0, 6);
  const num = Number.parseInt(base, 16);
  const amt = Math.round(2.55 * percent);
  const r = (num >> 16) + amt;
  const g = ((num >> 8) & 0xff) + amt;
  const b = (num & 0xff) + amt;

  return `#${(
    0x1000000 +
    (r < 255 ? (r < 1 ? 0 : r) : 255) * 0x10000 +
    (g < 255 ? (g < 1 ? 0 : g) : 255) * 0x100 +
    (b < 255 ? (b < 1 ? 0 : b) : 255)
  )
    .toString(16)
    .slice(1)}`;
}

function derivarCorClaraDoFundo(main: string): string {
  const { hex, alpha } = separarHexComAlpha(main);
  const clara = clarearCor(hex, BG_DEFAULT_LIGHTEN);
  return alpha ? `${clara}${alpha}` : clara;
}

function parseBool(value: unknown, fallback = false): boolean {
  if (value == null || value === "") return fallback;
  return value === true || value === 1 || String(value) === "true" || String(value) === "1";
}

function parseNum(value: unknown, fallback = 0): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function persistirCache(cfg: ConfigVisual) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(cfg));
  } catch {
    /* cache opcional */
  }
}

function lerCache(): ConfigVisual | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? (JSON.parse(raw) as ConfigVisual) : null;
  } catch {
    return null;
  }
}

function lerGradienteBotoesSalvo(): boolean {
  try {
    return localStorage.getItem("customColorGradient") !== "false";
  } catch {
    return true;
  }
}

export function aplicarGradienteBotoes(ativo = lerGradienteBotoesSalvo()): void {
  document.documentElement.classList.toggle("ui-gradiente-botoes", ativo);
}

export function aplicarBotoesRetangulares(ativo: boolean): void {
  document.documentElement.classList.toggle("ui-botoes-retangulares", ativo);
}

/**
 * Mantém o efeito Glass em uma única fonte de verdade. O atributo controla as
 * superfícies e a variável é consumida também pelos cards dos dashboards
 * quando o fundo em gradiente estiver ligado.
 */
export function aplicarGlass(ativo: boolean, intensidade: number): void {
  const root = document.documentElement;
  root.dataset.glass = ativo ? "true" : "false";
  root.style.setProperty("--glass-intensidade", String(clamp(intensidade, 0, 100)));
}

export function aplicarAcento(corMain: string, corLight?: string): void {
  const root = document.documentElement;
  const main = corValida(corMain);
  if (!main) return;
  const light = corValida(corLight) || main;
  root.style.setProperty("--accent-main", main);
  root.style.setProperty("--accent-light", light);
  try {
    localStorage.setItem("--accent-main", main);
    localStorage.setItem("accentMain", main);
    localStorage.setItem("--accent-light", light);
    localStorage.setItem("accentLight", light);
  } catch {
    /* localStorage indisponivel */
  }
}

function resolverAcento(cfg: ConfigVisual): { main: string; light: string } | null {
  const base = corValida(cfg.corPerfil);
  if (!base) return null;
  if (!parseBool(cfg.marcaEmpresaPersonalizada)) {
    return { main: base, light: corValida(cfg.corLight) || base };
  }

  const tonalidade = clamp(parseNum(cfg.tonalidadeCor, 0), -50, 40);
  const intensidadeGradiente = clamp(parseNum(cfg.observacoes, 40), 1, 100);
  const intensidadeCor = clamp(parseNum(cfg.intensidadeCor, 100), 10, 100);
  const corComTonalidade = clarearCor(base, tonalidade);
  const alpha = Math.round((intensidadeCor / 100) * 255)
    .toString(16)
    .padStart(2, "0");
  const main = intensidadeCor < 100 ? `${corComTonalidade}${alpha}` : corComTonalidade;
  const corClara = clarearCor(corComTonalidade, intensidadeGradiente);
  const lightComAlpha = intensidadeCor < 100 ? `${corClara}${alpha}` : corClara;
  const light = lerGradienteBotoesSalvo() ? lightComAlpha : main;
  return { main, light };
}

function resolverCoresFundo(cfg: ConfigVisual): readonly [string, string] {
  const main = corValida(cfg.corPerfil) || "#36AEBF";
  const paleta = BG_PALETTES[String(cfg.bgGradient || "default")] || BG_PALETTES.default;
  const cor1 = paleta[0] || derivarCorClaraDoFundo(main);
  const cor2 = paleta[1] || main;
  return parseBool(cfg.inverterGradient) ? [cor2, cor1] : [cor1, cor2];
}

function valorDefinido(valor: unknown): boolean {
  return valor !== undefined && valor !== null && valor !== "";
}

function resolverPosicao(offsetXRaw: unknown, offsetYRaw: unknown, padraoX: number, padraoY: number) {
  const offsetX = clamp(parseNum(offsetXRaw, 0), -400, 400) / 4;
  const offsetY = clamp(parseNum(offsetYRaw, 0), -400, 400) / 4;
  return {
    x: clamp(padraoX + offsetX, -40, 140),
    y: clamp(padraoY + offsetY, -40, 140),
  };
}

export function aplicarFundoGradiente(cfg: ConfigVisual): void {
  const root = document.documentElement;
  const gradienteOn = parseBool(cfg.statusGradiente);
  const legado1 = parseNum(cfg.bgGradient1XY, 0);
  const legado2 = parseNum(cfg.bgGradient2XY, 0);
  const pos1X = valorDefinido(cfg.bgGradient1X)
    ? cfg.bgGradient1X
    : parseBool(cfg.bgGradient1Vertical) ? 0 : legado1;
  const pos1Y = valorDefinido(cfg.bgGradient1Y)
    ? cfg.bgGradient1Y
    : parseBool(cfg.bgGradient1Vertical) ? legado1 : 0;
  const pos2X = valorDefinido(cfg.bgGradient2X)
    ? cfg.bgGradient2X
    : parseBool(cfg.bgGradient2Vertical) ? 0 : legado2;
  const pos2Y = valorDefinido(cfg.bgGradient2Y)
    ? cfg.bgGradient2Y
    : parseBool(cfg.bgGradient2Vertical) ? legado2 : 0;
  const tamanho1Base = clamp(
    parseNum(valorDefinido(cfg.bgGradient1Size) ? cfg.bgGradient1Size : cfg.bgGradientSize, 100),
    50,
    200
  );
  const tamanho2Base = clamp(
    parseNum(valorDefinido(cfg.bgGradient2Size) ? cfg.bgGradient2Size : cfg.bgGradientSize, 100),
    50,
    200
  );
  const [cor1, cor2] = resolverCoresFundo(cfg);
  const pos1 = resolverPosicao(pos1X, pos1Y, -8, -12);
  const pos2 = resolverPosicao(pos2X, pos2Y, 112, 115);
  const size1 = Math.round(540 + tamanho1Base * 6);
  const size2 = Math.round(880 + tamanho2Base * 7);

  root.classList.remove("fundo-gradiente");
  root.classList.toggle("fundo-gradiente-home", gradienteOn);
  root.style.setProperty("--bg-gradient-color-1", cor1);
  root.style.setProperty("--bg-gradient-color-2", cor2);
  root.style.setProperty("--bg-gradient-size-1", `${size1}px`);
  root.style.setProperty("--bg-gradient-size-2", `${size2}px`);
  root.style.setProperty("--bg-gradient-pos-1-x", `${pos1.x}%`);
  root.style.setProperty("--bg-gradient-pos-1-y", `${pos1.y}%`);
  root.style.setProperty("--bg-gradient-pos-2-x", `${pos2.x}%`);
  root.style.setProperty("--bg-gradient-pos-2-y", `${pos2.y}%`);

  try {
    localStorage.setItem("fundoGradiente", gradienteOn ? "true" : "false");
    localStorage.setItem("--bg-gradient-color-1", cor1);
    localStorage.setItem("--bg-gradient-color-2", cor2);
  } catch {
    /* ignore */
  }
}

export function aplicarFundoSidebar(cfg: ConfigVisual): void {
  const root = document.documentElement;
  const selecao = String(cfg.corSidebar || "default").trim();
  const gradiente = SIDEBAR_GRADIENTS[selecao];
  const corLegada = corValida(selecao);

  if (!gradiente && !corLegada) {
    root.style.removeProperty("--sidebar-background-custom");
    return;
  }

  const opacidade = clamp(parseNum(cfg.opacidadeSidebar, 100), 0, 100);
  const cores = (gradiente || [corLegada]).map(
    (cor) => `color-mix(in srgb, ${cor} ${opacidade}%, var(--sidebar-base-background, var(--sidebar)))`
  );
  root.style.setProperty(
    "--sidebar-background-custom",
    gradiente ? `linear-gradient(135deg, ${cores.join(", ")})` : cores[0]
  );
}

export function aplicarConfigVisual(cfg: ConfigVisual): void {
  const acento = resolverAcento(cfg);
  if (acento) aplicarAcento(acento.main, acento.light);
  aplicarGradienteBotoes();
  aplicarBotoesRetangulares(parseBool(cfg.botoesRetangulares, false));
  const glass = parseBool(cfg.glass, false);
  const glassIntensidade = clamp(parseNum(cfg.glassIntensidade, 60), 0, 100);
  aplicarGlass(glass, glassIntensidade);

  sincronizarConfiguracoesSistemaPublicas({
    ...(typeof cfg.fantasia === "string" ? { fantasia: cfg.fantasia } : {}),
    modulosAtivos: String(cfg.modulosAtivos || "")
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean),
    loginSocial:
      cfg.loginSocial == null || cfg.loginSocial === ""
        ? true
        : parseBool(cfg.loginSocial, true),
    cadastroLogin:
      cfg.cadastroLogin == null || cfg.cadastroLogin === ""
        ? true
        : parseBool(cfg.cadastroLogin, true),    tourBoasVindas:
      cfg.tourBoasVindas == null || cfg.tourBoasVindas === ""
        ? true
        : parseBool(cfg.tourBoasVindas, true),
    glass,
    glassIntensidade,
  });

  aplicarFundoGradiente(cfg);
  aplicarFundoSidebar(cfg);
  persistirCache(cfg);
}

export async function carregarConfigVisual(
  opcoes: { usarCache?: boolean; codEmpresa?: string | number } = {}
): Promise<void> {
  if (opcoes.usarCache !== false) {
    const cache = lerCache();
    if (cache) aplicarConfigVisual(cache);
  }

  try {
    const res = await fetch("/api/rpc/obterConfiguracoesVisuais", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      cache: "no-store",
      body: JSON.stringify({ args: [String(opcoes.codEmpresa ?? lerEmpresaLoginSelecionada())] }),
    });
    if (!res.ok) return;

    const cfg = (await res.json()) as ConfigVisual | null;
    if (!cfg) return;
    aplicarConfigVisual(cfg);
  } catch {
    /* offline / nao autenticado */
  }
}

export async function recarregarIdentidadeVisualEmpresa(codEmpresa?: string | number): Promise<void> {
  await carregarConfigVisual({ usarCache: false, codEmpresa });
  notificarAssetsSistemaAtualizados();
}
